<script setup>
import { computed } from 'vue';
import { formatBytes } from 'shared/helpers/FileHelper';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
  variant: {
    type: String,
    default: 'compact',
  },
});

const emit = defineEmits(['removeAttachment']);

const nonRecordedAudioAttachments = computed(() => {
  return props.attachments.filter(attachment => !attachment?.isRecordedAudio);
});

const recordedAudioAttachments = computed(() =>
  props.attachments.filter(attachment => attachment.isRecordedAudio)
);

const onRemoveAttachment = itemIndex => {
  emit(
    'removeAttachment',
    nonRecordedAudioAttachments.value
      .filter((_, index) => index !== itemIndex)
      .concat(recordedAudioAttachments.value)
  );
};

const formatFileSize = file => {
  const size = file.byte_size || file.size;
  return formatBytes(size, 0);
};

const isTypeImage = file => {
  const type = file.content_type || file.type;
  return type.includes('image');
};

const fileName = file => {
  return file.filename || file.name;
};
</script>

<template>
  <div
    v-if="variant === 'large'"
    class="flex max-h-[13rem] gap-3 overflow-auto pb-1"
  >
    <div
      v-for="(attachment, index) in nonRecordedAudioAttachments"
      :key="attachment.id"
      class="group relative w-24 shrink-0"
    >
      <div
        class="grid aspect-square w-24 place-content-center overflow-hidden rounded-2xl border border-n-weak bg-n-slate-2 shadow-sm"
      >
        <img
          v-if="isTypeImage(attachment.resource)"
          class="h-full w-full object-cover"
          :src="attachment.thumb"
        />
        <span v-else class="i-lucide-file size-8 text-n-slate-10" />
      </div>
      <Button
        ghost
        slate
        xs
        icon="i-lucide-x"
        class="!absolute -right-2 -top-2 !rounded-full !bg-white !shadow-md"
        @click="onRemoveAttachment(index)"
      />
      <span
        class="mt-2 block truncate text-center text-xs font-medium text-n-slate-11"
      >
        {{ fileName(attachment.resource) }}
      </span>
      <span class="block text-center text-[11px] text-n-slate-9">
        {{ formatFileSize(attachment.resource) }}
      </span>
    </div>
  </div>
  <div v-else class="flex overflow-auto max-h-[12.5rem]">
    <div
      v-for="(attachment, index) in nonRecordedAudioAttachments"
      :key="attachment.id"
      class="flex items-center p-1 bg-n-slate-3 gap-1 rounded-md w-[15rem] mb-1"
    >
      <div class="max-w-[4rem] flex-shrink-0 w-6 flex items-center">
        <img
          v-if="isTypeImage(attachment.resource)"
          class="object-cover w-6 h-6 rounded-sm"
          :src="attachment.thumb"
        />
        <span
          v-else
          class="i-lucide-file relative size-6 text-lg text-n-slate-10"
        />
      </div>
      <div class="max-w-3/5 min-w-[50%] overflow-hidden text-ellipsis">
        <span
          class="h-4 overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap"
        >
          {{ fileName(attachment.resource) }}
        </span>
      </div>
      <div class="w-[30%] justify-center">
        <span class="overflow-hidden text-xs text-ellipsis whitespace-nowrap">
          {{ formatFileSize(attachment.resource) }}
        </span>
      </div>
      <div class="flex items-center justify-center">
        <Button
          ghost
          slate
          xs
          icon="i-lucide-x"
          @click="onRemoveAttachment(index)"
        />
      </div>
    </div>
  </div>
</template>
